import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, Router, RouterStateSnapshot, UrlTree } from '@angular/router';
import { environment } from '@environments/environment';
import { URL_PARAMS } from '@global/constants';
import { Flow, RoutePaths } from '@global/routePaths';
import { isQuickQuote } from '@global/utils';
import { LoadingService } from '@services/loading/loading.service';
import cloneDeep from 'clone-deep';
import { Observable, from } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class PageGuard implements CanActivate {
  readonly MOCKED = 'mocked';

  private continueToErrorPage = false;
  private continueToMaintenancePage = false;

  campaignCode: string;
  promotionCode: string;
  referralCode: string;

  utmSource: string;
  utmMedium: string;
  utmCampaign: string;
  utmTerm: string;
  utmContent: string;
  utmId: string;
  partnerId: string;
  gclid: string;
  clickId: string;
  dclid: string;
  gadSource: string;
  fbclid: string;
  msclkid: string;
  gtmDebug: string;
  twclid: string;
  ttclid: string;
  sId: string;
  qs: string;
  ak: string;
  entrypoint: string;
  empfehlung: string;
  ch: string;
  einsprungsquelle: string;
  nr: string;
  empfaenger: string;

  userId: string;
  projectId: string;
  policyId: string;
  isBackOfficeUser: boolean;
  version: string;

  vehicleType: string;
  plateNumber: string;
  birthdate: string;
  privacyConsent: string;
  skipPartnership: boolean = true;

  flow: string;
  id: string;

  private cookies: Map<string, string>;

  constructor(
    private readonly router: Router,
    private readonly loadingService: LoadingService
  ) {}

  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree {
    this.continueToErrorPage = this.router.currentNavigation()?.extras?.state?.continueToErrorPage ?? false;

    this.continueToMaintenancePage =
      this.router.currentNavigation()?.extras?.state?.continueToMaintenancePage ?? false;

    if (environment.environmentName === 'SND' && state.url === '/maintenance-page') {
      this.continueToMaintenancePage = true;
    }

    this.campaignCode = route.queryParams?.campaignCode;
    this.promotionCode = route.queryParams?.promotionCode;
    this.referralCode = route.queryParams?.referralCode?.slice(0, 20);

    this.utmSource = route.queryParams?.[URL_PARAMS.utm_source];
    this.utmMedium = route.queryParams?.[URL_PARAMS.utm_medium];
    this.utmCampaign = route.queryParams?.[URL_PARAMS.utm_campaign];
    this.utmTerm = route.queryParams?.[URL_PARAMS.utm_term];
    this.utmContent = route.queryParams?.[URL_PARAMS.utm_content];
    this.utmId = route.queryParams?.[URL_PARAMS.utm_id];
    this.partnerId = route.queryParams?.[URL_PARAMS.partner_id];
    this.gclid = route.queryParams?.[URL_PARAMS.gclid];
    this.clickId = route.queryParams?.[URL_PARAMS.click_id];
    this.dclid = route.queryParams?.[URL_PARAMS.dclid];
    this.gadSource = route.queryParams?.[URL_PARAMS.gad_source];
    this.fbclid = route.queryParams?.[URL_PARAMS.fbclid];
    this.msclkid = route.queryParams?.[URL_PARAMS.msclkid];
    this.gtmDebug = route.queryParams?.[URL_PARAMS.gtm_debug];
    this.twclid = route.queryParams?.[URL_PARAMS.twclid];
    this.ttclid = route.queryParams?.[URL_PARAMS.ttclid];
    this.sId = route.queryParams?.[URL_PARAMS.s_id];
    this.qs = route.queryParams?.[URL_PARAMS.qs];
    this.ak = route.queryParams?.[URL_PARAMS.ak];
    this.entrypoint = route.queryParams?.[URL_PARAMS.entrypoint];
    this.empfehlung = route.queryParams?.[URL_PARAMS.empfehlung];
    this.ch = route.queryParams?.[URL_PARAMS.ch];
    this.einsprungsquelle = route.queryParams?.[URL_PARAMS.einsprungsquelle];
    this.nr = route.queryParams?.[URL_PARAMS.nr];
    this.empfaenger = route.queryParams?.[URL_PARAMS.empfaenger];

    return this.continueToErrorPage
      ? this.loadingService.dismissLoading().then(() => this.continueToErrorPage)
      : this.continueToMaintenancePage
        ? this.loadingService.dismissLoading().then(() => this.continueToMaintenancePage)
        : this.checkData();
  }

  private checkData(): Observable<boolean> {
    this.cookies = this.getCookies();

    return from(this.loadingService.presentLoading()).pipe(
      switchMap(() => this.redirectPage()),
      catchError(() => this.goToErrorPage())
    );
  }

  private redirectPage(): Observable<boolean> | Promise<boolean> {
    return isQuickQuote() ? this.quickQuote() : this.newQuotation();
  }

  newQuotation(): Promise<boolean> {
    const nextRoute = RoutePaths.NEW_QUOTATION.VEHICLE_INFORMATION.INSURANCE_SITUATION.route;
    return this.navigateRoute(nextRoute, { flow: Flow.NEW_QUOTATION });
  }

  quickQuote(): Promise<boolean> {
    const nextRoute = RoutePaths.QUICK_QUOTE.QUICK_QUOTE.INSURANCE_HOLDER_DATA.route;
    return this.navigateRoute(nextRoute, { flow: Flow.QUICK_QUOTE });
  }

  private navigateRoute(route: string, payload?: Record<string, unknown>): Promise<boolean> {
    return this.router
      .navigate([route], {
        queryParamsHandling: 'merge',
        queryParams: {
          campaignCode: this.campaignCode,
          promotionCode: this.promotionCode,
          referralCode: this.referralCode ?? this.empfehlung,
          [URL_PARAMS.utm_source]: this.utmSource,
          [URL_PARAMS.utm_medium]: this.utmMedium,
          [URL_PARAMS.utm_campaign]: this.utmCampaign,
          [URL_PARAMS.utm_term]: this.utmTerm,
          [URL_PARAMS.utm_content]: this.utmContent,
          [URL_PARAMS.utm_id]: this.utmId,
          [URL_PARAMS.partner_id]: this.partnerId,
          [URL_PARAMS.gclid]: this.gclid,
          [URL_PARAMS.click_id]: this.clickId,
          [URL_PARAMS.dclid]: this.dclid,
          [URL_PARAMS.gad_source]: this.gadSource,
          [URL_PARAMS.fbclid]: this.fbclid,
          [URL_PARAMS.msclkid]: this.msclkid,
          [URL_PARAMS.gtm_debug]: this.gtmDebug,
          [URL_PARAMS.twclid]: this.twclid,
          [URL_PARAMS.ttclid]: this.ttclid,
          [URL_PARAMS.s_id]: this.sId,
          [URL_PARAMS.qs]: this.qs,
          [URL_PARAMS.ak]: this.ak,
          [URL_PARAMS.entrypoint]: this.entrypoint,
          [URL_PARAMS.empfehlung]: this.empfehlung,
          [URL_PARAMS.ch]: this.ch,
          [URL_PARAMS.einsprungsquelle]: this.einsprungsquelle,
          [URL_PARAMS.nr]: this.nr,
          [URL_PARAMS.empfaenger]: this.empfaenger,
        },
        state: { ...payload, allowNavigation: true },
      })
      .finally(() => this.loadingService.dismissLoading())
      .then(() => false);
  }

  private goToErrorPage(): Promise<boolean> {
    return this.navigateRoute(RoutePaths.GENERIC_ERROR.route, { continueToErrorPage: true });
  }

  private goToMaintenancePage(): Promise<boolean> {
    return this.navigateRoute(RoutePaths.MAINTENANCE_PAGE.route, { continueToMaintenancePage: true });
  }

  protected cloneDeep<T>(value: T): T {
    // return window.structuredClone(value);
    return cloneDeep(value);
  }

  private getCookies(): Map<string, string> {
    const COOKIE_SEPARATOR = ';';
    const COOKIE_VALUE_SEPARATOR = '=';

    const rawList = document.cookie.split(COOKIE_SEPARATOR);
    const cookiePairs = rawList.map(cookiePair => cookiePair.split(new RegExp(`${COOKIE_VALUE_SEPARATOR}(.*)`, 's')));
    return cookiePairs.reduce((prev, [key, value]) => {
      prev.set(key.trim(), value);
      return prev;
    }, new Map<string, string>());
  }
}
